/**
 * A persistent body-level host for overlay portals (Select / Popover dropdowns)
 * that must stay REACHABLE when opened INSIDE a modal Dialog (bug CC-21).
 *
 * Kobalte's modal Dialog runs `ariaHideOutside`, which sets `aria-hidden="true"`
 * on every top-level body subtree except the dialog. Its MutationObserver only
 * RE-INCLUDES newly added body nodes tagged `data-live-announcer` /
 * `data-react-aria-top-layer` — it does NOT honour Kobalte's own
 * `data-kb-top-layer`. So a Select/Popover portal opened AFTER the dialog is
 * mounted (portal container added first, hidden; content added inside it is then
 * ignored because its parent is already in the hidden set) ends up
 * `aria-hidden`: its `role="option"` items vanish from the a11y tree — invisible
 * to screen readers and unclickable in e2e — even though z-index puts them on top.
 *
 * Mounting those portals into a single persistent host tagged
 * `data-live-announcer="true"` keeps the host (and its whole subtree) visible in
 * BOTH the initial walk (`querySelectorAll("[data-live-announcer], …")`) and the
 * observer (`dataset.liveAnnouncer === "true"`). The host lives at body level, so
 * there's no `overflow-hidden` clipping from the dialog, and the dropdown keeps
 * its usual floating-ui positioning. The `data-live-announcer` attribute carries
 * no ARIA semantics on its own (no `aria-live`), so it doesn't affect AT output.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
let host: HTMLElement | undefined;

export function overlayPortalHost(): HTMLElement | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  if (host?.isConnected) {
    return host;
  }
  host = document.createElement("div");
  // The single marker honoured by ariaHideOutside on BOTH its initial walk and
  // its MutationObserver, so overlays stay reachable inside a modal Dialog.
  host.dataset.liveAnnouncer = "true";
  host.dataset.overlayPortalHost = "true";
  document.body.append(host);
  return host;
}
